import { useQuery } from '@tanstack/react-query';
import { useSelector, useStore } from 'react-redux';
import { firstValueFrom } from 'rxjs';

import { useOwnerParams } from 'hooks/use-owner-params.hook';
import { apiClient } from 'store/api-client';
import { RootState } from 'store/reducer';

export const FAVORITE_RESOURCE_UUIDS_QUERY_KEY = 'favorite-resource-uuids';

// Favorites are fetched a page at a time and every page is walked: the map needs the complete set,
// since a favorite left out would be dimmed and show an unfilled star.
const LIMIT = 100;

// Backstop against a response whose `total` never settles, so the walk always ends.
const MAX_PAGES = 100;

const EMPTY: ReadonlySet<string> = new Set();

interface UseFavoriteResourceUuidsQueryParams {
  enabled?: boolean;
}

interface UseFavoriteResourceUuidsQueryValues {
  uuids: ReadonlySet<string>;
  /**
   * The set is complete only once this is true. Before it — loading, disabled, or failed — `uuids`
   * is empty because nothing is known, not because the user has no favorites.
   */
  isLoaded: boolean;
  isError: boolean;
  refetch: VoidFunction;
}

/**
 * The uuids of the signed-in user's favorited resources in the current building
 * (`GET .../resources?favorites_only=true`, the call the list's "favorites only" filter makes), for
 * the native map to match placements against. The map's own data comes from resource-booking-service,
 * which knows nothing about favorites — they live in helix.
 */
export const useFavoriteResourceUuidsQuery = ({
  enabled = true,
}: UseFavoriteResourceUuidsQueryParams = {}): UseFavoriteResourceUuidsQueryValues => {
  const store = useStore<RootState>();
  const buildingUuid = useSelector((state: RootState) => state.config.buildingUuid);
  const { ownerType, ownerUuid } = useOwnerParams();

  const { data, isSuccess, isError, refetch } = useQuery({
    queryKey: [FAVORITE_RESOURCE_UUIDS_QUERY_KEY, buildingUuid, ownerType, ownerUuid],
    queryFn: async (): Promise<ReadonlySet<string>> => {
      const uuids = new Set<string>();

      /* eslint-disable no-await-in-loop -- each page's offset depends on how many came back before it */
      for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex += 1) {
        const response = await firstValueFrom(
          apiClient(store.getState()).getResources(ownerType, ownerUuid, {
            favorites_only: true,
            limit: LIMIT,
            offset: uuids.size,
          }),
        );
        const page = response.response?.data?.resources ?? [];
        const sizeBefore = uuids.size;
        page.forEach(({ uuid }) => uuids.add(uuid));

        const total = response.response?.total ?? response.response?.data?.total ?? 0;
        // A page that added nothing new also ends the walk — it cannot move the offset forward.
        if (page.length < LIMIT || uuids.size >= total || uuids.size === sizeBefore) return uuids;
      }
      /* eslint-enable no-await-in-loop */

      throw new Error('Favorite resources pagination did not end');
    },
    enabled: enabled && !!buildingUuid,
  });

  return { uuids: data ?? EMPTY, isLoaded: isSuccess, isError, refetch: () => void refetch() };
};
